import Icon from "@/components/Icon";
import { chakra, Box, ChakraProps, Flex, Spacer, Text, Input } from "@chakra-ui/react";
import { ReactComponent as selectedIcon } from "@/assets/icons/vip_selected.svg";
import { ReactComponent as MinusButtonSvg } from "@/assets/icons/minus_button.svg";
import { ReactComponent as PlusButtonSvg } from "@/assets/icons/plus_button.svg";
import { useCallback, useEffect, useState } from "react";
interface CardProps {
  title: string;
  oldPrice: string;
  curPrice: string;
  selected?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  unitPrice?: string;
  periodPrice?: string;
  custom?: boolean;
  customVal?: number;
  onChange?(val: number): void;
}

const goldGradient = `linear-gradient(90deg, #F0BF68, #F9E5AE);`;
const defaultBgColor = "#404040";
const goldBorder: ChakraProps = {
  _before: {
    content: "''",
    bg: goldGradient,
    pos: "absolute",
    left: 0,
    top: 0,
  },
  bg: goldGradient,
};

const iconButtonStyle = (disabled: boolean) => ({
  color: disabled ? "text.primary" : "line.darkGray",
  fontSize: "18px",
  cursor: disabled ? "not-allowed" : "pointer",
});

export default function PriceCard(props: CardProps) {
  const {
    title,
    oldPrice,
    curPrice,
    selected = false,
    className,
    onClick,
    unitPrice = "",
    periodPrice = "",
    custom = false,
    onChange,
    customVal = 1000,
  } = props;
  const [inputVal, setInputVal] = useState(String(customVal));

  useEffect(() => {
    setInputVal(String(customVal));
  }, [customVal]);

  const inputValueFormatter = (input: number) => {
    const step = 1;
    let val = input / 100;
    if (val <= 1) {
      val = 1;
    } else if (val >= 3300) {
      val = 3300;
    } else {
      if ((val % 1) / step < 0.5) {
        val = val - (val % step);
      } else {
        val = val + (step - (val % step));
      }
      val = +val.toFixed(0);
    }
    return val * 100;
  };

  const handleInputChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      setInputVal(e.target.value);
    },
    [setInputVal],
  );

  const handleInputBlur = useCallback(
    (inputVal: number) => {
      const val = inputValueFormatter(inputVal);
      setInputVal(val.toString());
      onChange?.(val);
    },
    [setInputVal, onChange],
  );

  const handleInputPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.code === "Enter") {
        const val = inputValueFormatter(+e.currentTarget.value);
        setInputVal(val.toString());
        onChange?.(val);
      }
    },
    [setInputVal, onChange],
  );

  const handleMinus = useCallback(() => {
    const val = inputValueFormatter(+inputVal - 100);
    setInputVal(val.toString());
    onChange?.(val);
  }, [inputVal, setInputVal, onChange]);

  const handlePlus = useCallback(() => {
    const val = inputValueFormatter(+inputVal + 100);
    setInputVal(val.toString());
    onChange?.(val);
  }, [inputVal, setInputVal, onChange]);

  return (
    <Flex
      width="100%"
      h="110px"
      pos="relative"
      rounded="6px"
      bg={defaultBgColor}
      border="2px solid transparent"
      cursor="pointer"
      className={className}
      onClick={onClick}
      {...(selected ? { ...goldBorder } : {})}
    >
      <Flex
        w="100%"
        p="16px"
        pos="relative"
        rounded="4px"
        bg={selected ? "#2D2D2D" : defaultBgColor}
      >
        <Box maxW="165px">
          <Text color="#DCDCDC" fontWeight={600} fontSize="14px" lineHeight="22px">
            {title}
          </Text>
          <Text mt="8px" color="#999999" fontSize="12px" lineHeight="12px">
            {custom
              ? `每星 ${unitPrice} 元，可输入任意整百倍星数:`
              : `每星 ${unitPrice} 元，约合每图 ${
                  Math.round(Number(unitPrice) * 2 * 100) / 100
                } 元`}
          </Text>
          <Text mt="8px" whiteSpace="nowrap">
            <Flex tabIndex={0} alignItems="center" w="100%">
              {custom ? (
                <>
                  <Icon
                    svg={MinusButtonSvg}
                    flex="none"
                    strokeByCurrentColor
                    {...iconButtonStyle(false)}
                    onClick={handleMinus}
                  />
                  <Input
                    w="50px"
                    minW="50px"
                    bg="transparent"
                    type="number"
                    h="22px"
                    size="unset"
                    p={0}
                    textAlign="center"
                    fontSize="12px"
                    border="1px solid"
                    borderColor="border.buttonOutline"
                    mx="8px"
                    _focusVisible={{ boxShadow: "none" }}
                    value={inputVal}
                    onBlur={(e: any) => handleInputBlur(+e.target.value)}
                    onKeyDown={handleInputPress}
                    onChange={handleInputChange}
                  />
                  <Icon
                    svg={PlusButtonSvg}
                    flex="none"
                    mr="0.5em"
                    strokeByCurrentColor
                    {...iconButtonStyle(false)}
                    onClick={handlePlus}
                  />
                </>
              ) : null}
              <Box>
                <chakra.span
                  bg={selected ? goldGradient : "brand.primary"}
                  display="inline-block"
                  height="100%"
                  bgClip="text"
                  fontWeight={600}
                  lineHeight="24px"
                  fontSize="12px"
                >
                  ¥
                </chakra.span>
                <chakra.span
                  bg={selected ? goldGradient : "brand.primary"}
                  bgClip="text"
                  fontWeight={600}
                  fontSize="24px"
                  lineHeight="24px"
                >
                  {custom ? customVal * Number(unitPrice) : curPrice}
                </chakra.span>
                <chakra.span
                  bg={selected ? goldGradient : "brand.primary"}
                  bgClip="text"
                  verticalAlign="text-bottom"
                  fontWeight={600}
                  fontSize="12px"
                >
                  {periodPrice}
                </chakra.span>
                <chakra.span
                  fontSize="12px"
                  color="#999999"
                  ml="1em"
                  textDecor={oldPrice && oldPrice !== "0" ? "line-through" : "none"}
                  verticalAlign="0.3em"
                  whiteSpace="nowrap"
                  fontWeight={400}
                >
                  {oldPrice && oldPrice !== "0" ? `原价¥${oldPrice}` : "\u00A0"}
                </chakra.span>
              </Box>
            </Flex>
          </Text>
        </Box>
        <Spacer />
        <Flex align="center" justify="flex-end">
          {selected ? (
            <Icon squared={false} attrs={{ width: 20, height: 20 }} svg={selectedIcon} />
          ) : (
            <Box w="20px" h="20px" />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
